import { peopleContactsService } from '../../services/peopleContacts'
import { pickCoverageForAppointment } from '../../services/supportCenterAgendaApi'
import { formatPhone } from './dashboardHelpers'
import type {
  SupportCenterAgendaEvent,
  SupportCenterAppointmentFormState,
} from '../../types/supportCenterAppointments'
import type {
  SupportCenterElegibilidadeCobertura,
  SupportCenterElegibilidadeEspecialidade,
} from '../../types/supportCenterAgenda'
import type { SupportCenterAssistance } from '../../types/supportCenterAssistances'
import type { SupportCenterSpecialization } from '../../types/supportCenterSpecialties'

export { pickCoverageForAppointment }

export const NO_ASSISTANCE_MESSAGE =
  'Não é possível agendar: o segurado não possui assistência para esta especialidade.'

/** Opções fixas de parentesco (paciente ≠ titular). */
export const RELATIONSHIP_TO_HOLDER_OPTIONS = ['Cônjuge', 'Filho'] as const

/** Une coberturas de todas as especialidades elegíveis, únicas por código. */
export function uniqueEligibleCoverages(
  especialidades: SupportCenterElegibilidadeEspecialidade[] | null | undefined
): SupportCenterElegibilidadeCobertura[] {
  if (!especialidades?.length) return []
  const seen = new Set<string>()
  const out: SupportCenterElegibilidadeCobertura[] = []
  for (const spec of especialidades) {
    for (const cobertura of spec.coberturas ?? []) {
      const code = cobertura.code.trim()
      if (!code || seen.has(code)) continue
      seen.add(code)
      out.push({
        code,
        name: cobertura.name.trim() || code,
        codApolice: cobertura.codApolice,
        codConvenio: cobertura.codConvenio,
        codFornecedorAssistencia: cobertura.codFornecedorAssistencia,
        codCorretor: cobertura.codCorretor,
        codCliente: cobertura.codCliente,
      })
    }
  }
  return out.sort((left, right) => {
    const byCode = left.code.localeCompare(right.code, 'pt-BR', {
      numeric: true,
      sensitivity: 'base',
    })
    if (byCode !== 0) return byCode
    return left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' })
  })
}

/**
 * Coberturas elegíveis restritas às especialidades do profissional
 * e, se informado, aos códigos de assistência do catálogo dessas especialidades.
 */
export function uniqueEligibleCoveragesForSpecialistScope(
  especialidades: SupportCenterElegibilidadeEspecialidade[] | null | undefined,
  specializationIds: Set<string>,
  allowedAssistanceCodes?: Set<string>
): SupportCenterElegibilidadeCobertura[] {
  const scoped = (especialidades ?? []).filter((spec) => specializationIds.has(spec.id))
  const coverages = uniqueEligibleCoverages(scoped)
  if (!allowedAssistanceCodes) return coverages
  return coverages.filter((cobertura) => allowedAssistanceCodes.has(cobertura.code.trim()))
}

/** Cobertura sintética (titular falecido): códigos PASI zerados para o POST. */
function zeroCoverage(
  code: string,
  name: string
): SupportCenterElegibilidadeCobertura {
  return {
    code,
    name: name.trim() || code,
    codApolice: 0,
    codConvenio: 0,
    codFornecedorAssistencia: 0,
    codCorretor: 0,
    codCliente: 0,
  }
}

/**
 * Catálogo sintético quando o titular está Falecido (sem elegibilidade PASI):
 * todas as assistências do catálogo × especialidades do escopo.
 */
export function buildDeceasedEligibilityCatalog(input: {
  specializations: SupportCenterSpecialization[]
  assistances: SupportCenterAssistance[]
  userSpecSet?: Set<string> | null
  isSpecialistScope?: boolean
}): {
  especialidadeIds: string[]
  especialidades: SupportCenterElegibilidadeEspecialidade[]
} {
  const allCoverages = input.assistances
    .map((row) => zeroCoverage(row.code.trim(), row.name))
    .filter((row) => row.code)
    .sort((a, b) => a.code.localeCompare(b.code, 'pt-BR', { numeric: true, sensitivity: 'base' }))

  const especialidades: SupportCenterElegibilidadeEspecialidade[] = []
  for (const spec of input.specializations) {
    if (input.isSpecialistScope && input.userSpecSet && !input.userSpecSet.has(spec.id)) {
      continue
    }
    // Todas as assistências do catálogo (titular falecido não tem filtro PASI).
    especialidades.push({
      id: spec.id,
      name: spec.name,
      form_kind: null,
      coberturas: allCoverages,
      profissionais: [],
    })
  }

  return {
    especialidadeIds: especialidades.map((spec) => spec.id),
    especialidades,
  }
}

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

export function emailsToList(emails: string[]): string[] {
  return emails.map((e) => e.trim().toLowerCase()).filter(Boolean)
}

export function toDatetimeLocal(value: string): string {
  if (!value) return ''
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) {
    const raw = value.replace(' ', 'T')
    return raw.length >= 16 ? `${raw.slice(0, 13)}:00` : raw.slice(0, 16)
  }
  const pad = (n: number) => String(n).padStart(2, '0')
  // Sempre hora cheia
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`
}

export function fromDatetimeLocal(value: string): string {
  if (!value) return ''
  const normalized = value.length === 16 ? `${value}:00` : value
  // Força minutos :00
  return normalized.replace(/T(\d{2}):\d{2}/, 'T$1:00')
}

export function datePart(scheduledAt: string): string {
  if (!scheduledAt) return ''
  return scheduledAt.slice(0, 10)
}

export function hourPart(scheduledAt: string): string {
  if (!scheduledAt || scheduledAt.length < 16) return ''
  return `${scheduledAt.slice(11, 13)}:00`
}

export function combineDateAndHour(dateYmd: string, hourHm: string): string {
  if (!dateYmd || !hourHm) return ''
  const hour = hourHm.slice(0, 2)
  return `${dateYmd}T${hour}:00`
}

/** Mantém data+hora quando o slot veio da grade; senão limpa só a hora. */
export function preserveScheduledAt(prevScheduledAt: string, slotLocked: boolean): string {
  if (slotLocked && hourPart(prevScheduledAt)) return prevScheduledAt
  return datePart(prevScheduledAt) ? `${datePart(prevScheduledAt)}T` : ''
}

export function appointmentToForm(
  event: SupportCenterAgendaEvent
): SupportCenterAppointmentFormState {
  const same = onlyDigits(event.holder_cpf) === onlyDigits(event.patient_cpf)
  const channel =
    event.channel === 'whatsapp' ||
    event.channel === 'telefone' ||
    event.channel === 'videochamada'
      ? event.channel
      : ''
  return {
    holder_cpf: formatCpf(event.holder_cpf),
    holder_status: 'ativo',
    holder_name: event.holder_people?.name ?? '',
    holder_birth_date: event.holder_people?.captured_birth_date?.slice(0, 10) ?? '',
    patient_cpf: formatCpf(event.patient_cpf),
    patient_name: event.patient_people?.name ?? '',
    patient_birth_date: event.patient_people?.captured_birth_date?.slice(0, 10) ?? '',
    patient_is_insured: same,
    relationship_to_holder: event.relationship_to_holder ?? '',
    internal_emails: emailsToList(event.internal_emails),
    client_emails: emailsToList(event.client_emails),
    patient_phone: formatPhone(event.patient_phone ?? ''),
    contact_owner: same ? 'holder' : 'patient',
    assistance_code: event.assistance?.code?.trim() || '',
    channel,
    specialization_id: event.specialization_id,
    professional_user_id: event.professional_user_id || event.professional?.id || '',
    scheduled_at: toDatetimeLocal(event.scheduled_at),
  }
}

export async function loadContactsPrefill(taxId: string): Promise<{
  phones: string[]
  emails: string[]
}> {
  const contacts = await peopleContactsService.listByPerson(taxId)
  const phones = contacts
    .filter((row) => row.contact_type === 'phone')
    .map((row) => formatPhone(row.value))
    .filter((value) => onlyDigits(value).length >= 10)
  const emails = contacts
    .filter((row) => row.contact_type === 'email')
    .map((row) => row.value.trim().toLowerCase())
    .filter(Boolean)
  return {
    phones: [...new Set(phones)],
    emails: [...new Set(emails)],
  }
}

/** Parse `yyyy-mm-dd` (ou ISO) para Date local ao meio-dia. */
export function parseIsoDateOnly(value: string | null | undefined): Date | null {
  const trimmed = (value ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return null
  const d = new Date(`${trimmed.slice(0, 10)}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Idade em anos completos a partir de `yyyy-mm-dd`. */
export function ageFromIsoDate(
  birthDateYmd: string | null | undefined,
  asOf: Date = new Date()
): number | null {
  const birth = parseIsoDateOnly(birthDateYmd)
  if (!birth) return null
  let age = asOf.getFullYear() - birth.getFullYear()
  const monthDiff = asOf.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birth.getDate())) {
    age -= 1
  }
  return age < 0 ? 0 : age
}

export function isMinorFromIsoDate(birthDateYmd: string | null | undefined): boolean {
  const age = ageFromIsoDate(birthDateYmd)
  return age !== null && age < 18
}

/** Converte `yyyy-mm-dd` → `dd-mm-yyyy` (formato da edge buscar-pessoa-cpf). */
export function isoDateToEdgeBirthdate(birthDateYmd: string | null | undefined): string | null {
  const trimmed = (birthDateYmd ?? '').trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null
  const [y, m, d] = trimmed.split('-')
  return `${d}-${m}-${y}`
}
